/**
 * The GST calculation engine.
 *
 * Pure functions, no side effects, no I/O — called by the browser for a live
 * invoice preview and by the server as the authority that actually gets
 * stored. Both sides import this exact code so they can never disagree about
 * a total; the server recomputes everything from these same functions rather
 * than trusting whatever the browser sent.
 *
 * The rules encoded here (see docs/project-plan.html §06 for the full
 * reasoning):
 *
 *   - Gold and silver jewellery both carry 3% GST.
 *   - Making charges are a RUPEE figure, never a percentage, and are folded
 *     into the line's taxable value — a composite supply whose principal
 *     supply is the jewellery, taxed once at 3% along with the metal and
 *     stones. There is no separate job-work line at 5%.
 *   - Same state as the shop -> CGST + SGST, split evenly. Different state ->
 *     IGST. This is derived from the two state codes, never chosen by hand.
 *   - Old gold handed over in exchange reduces what the customer PAYS, not
 *     the taxable value. GST is charged on the full value of the new
 *     jewellery — getting this backwards understates output tax.
 */
import { z } from 'zod';
import Decimal from 'decimal.js';
import { money, round2, sum, toAmount } from './money.js';
import { round3 } from './weight.js';

const HALF_GST = (rate) => money(rate).dividedBy(2);

/**
 * Formats a GST rate to 2 decimal places for the common case (3, 1.5, 0.75 —
 * everything this shop billed before stone HSN codes existed), but keeps a
 * 3rd decimal place when the rate actually needs it (0.125% precious stones,
 * its 0.063% CGST/SGST half). Never round these to 2dp unconditionally — that
 * silently turns 0.125 into 0.13 on a GST invoice.
 */
function formatRate(rate) {
  const threeDp = money(rate).toFixed(3);
  return threeDp.endsWith('0') ? threeDp.slice(0, -1) : threeDp;
}

/**
 * One invoice line. Every rupee figure here is computed once, in this order,
 * and never re-derived a different way elsewhere:
 *
 *   wastageWeight = netWeight × wastagePercentage ÷ 100
 *   billedWeight = netWeight + wastageWeight
 *   metalValue = billedWeight × ratePerGram
 *   makingCharges = flat rupees, or (perGramRate × netWeight) resolved to rupees
 *   taxableValue = metalValue + makingCharges + stoneCharges + otherCharges − itemDiscount
 *   tax = taxableValue × gstRate, split into CGST+SGST or IGST
 *   lineTotal = taxableValue + tax
 *
 * Wastage accounts for metal lost in melting/working — the customer is billed
 * for slightly more weight than what's actually in the piece. It inflates the
 * priced (billed) weight only; `netWeight` keeps meaning the item's true
 * physical weight, which is what stock and buy-back valuation care about.
 * Making charges, when entered per gram, are still resolved against the true
 * netWeight — wastage is metal, not labour.
 *
 * @param {object} input
 * @param {number|string} input.netWeight grams
 * @param {number|string} input.ratePerGram
 * @param {number|string} [input.wastagePercentage] percent of netWeight, defaults to 0
 * @param {'flat'|'per_gram'} input.makingType
 * @param {number|string} input.makingValue rupees, or rupees/gram when makingType is 'per_gram'
 * @param {number|string} [input.stoneCharges]
 * @param {number|string} [input.otherCharges]
 * @param {number|string} [input.itemDiscount]
 * @param {number|string} [input.gstRate] percent, defaults to 3
 * @param {'intra'|'inter'} input.supplyType
 */
export function calcInvoiceLine(input) {
  const netWeight = round3(input.netWeight ?? 0);
  const wastagePercentage = money(input.wastagePercentage ?? 0);
  const wastageWeight = round3(netWeight.times(wastagePercentage).dividedBy(100));
  const billedWeight = round3(netWeight.plus(wastageWeight));
  const ratePerGram = money(input.ratePerGram ?? 0);
  const gstRate = money(input.gstRate ?? 3);

  const metalValue = round2(billedWeight.times(ratePerGram));

  const makingCharges =
    input.makingType === 'per_gram'
      ? round2(money(input.makingValue ?? 0).times(netWeight))
      : round2(input.makingValue ?? 0);

  const stoneCharges = round2(input.stoneCharges ?? 0);
  const otherCharges = round2(input.otherCharges ?? 0);
  const itemDiscount = round2(input.itemDiscount ?? 0);

  const taxableValue = round2(
    metalValue.plus(makingCharges).plus(stoneCharges).plus(otherCharges).minus(itemDiscount),
  );

  const tax = calcTax(taxableValue, gstRate, input.supplyType);

  const lineTotal = round2(taxableValue.plus(tax.totalTax));

  return {
    netWeight: netWeight.toFixed(3),
    wastagePercentage: wastagePercentage.toFixed(2),
    wastageWeight: wastageWeight.toFixed(3),
    billedWeight: billedWeight.toFixed(3),
    ratePerGram: toAmount(ratePerGram),
    metalValue: toAmount(metalValue),
    makingCharges: toAmount(makingCharges),
    stoneCharges: toAmount(stoneCharges),
    otherCharges: toAmount(otherCharges),
    itemDiscount: toAmount(itemDiscount),
    taxableValue: toAmount(taxableValue),
    gstRate: formatRate(gstRate),
    ...tax,
    lineTotal: toAmount(lineTotal),
  };
}

/** Splits tax on a taxable value into CGST+SGST (same state) or IGST (different state). */
export function calcTax(taxableValue, gstRate, supplyType) {
  const value = money(taxableValue);
  const rate = money(gstRate);

  if (supplyType === 'inter') {
    const igstAmount = round2(value.times(rate).dividedBy(100));
    return {
      cgstRate: '0.00',
      cgstAmount: '0.00',
      sgstRate: '0.00',
      sgstAmount: '0.00',
      igstRate: formatRate(rate),
      igstAmount: toAmount(igstAmount),
      totalTax: igstAmount,
    };
  }

  const halfRate = HALF_GST(rate);
  const cgstAmount = round2(value.times(halfRate).dividedBy(100));
  const sgstAmount = round2(value.times(halfRate).dividedBy(100));
  return {
    cgstRate: formatRate(halfRate),
    cgstAmount: toAmount(cgstAmount),
    sgstRate: formatRate(halfRate),
    sgstAmount: toAmount(sgstAmount),
    igstRate: '0.00',
    igstAmount: '0.00',
    totalTax: cgstAmount.plus(sgstAmount),
  };
}

/**
 * Same state as the shop -> intra-state (CGST+SGST). Different -> inter-state
 * (IGST). Derived from state codes so the counter never chooses this by hand —
 * the single most common data-entry error on a jewellery bill.
 */
export function determineSupplyType(shopStateCode, customerStateCode) {
  if (!customerStateCode) return 'intra'; // no GSTIN, no declared state -> assume local retail sale
  return String(shopStateCode) === String(customerStateCode) ? 'intra' : 'inter';
}

/**
 * Rolls up every line into invoice-level totals, applies the overall
 * discount, and reduces the payable amount (never the taxable value) by any
 * old gold exchanged or chit balance adjusted.
 *
 * @param {object[]} lines  output of calcInvoiceLine, one per row
 * @param {object} [adjustments]
 * @param {number|string} [adjustments.discountAmount] invoice-level discount, deducted from taxable value before tax is NOT re-derived — see note below
 * @param {number|string} [adjustments.oldGoldValue] reduces amount payable, not taxable value
 * @param {number|string} [adjustments.chitAdjustValue] reduces amount payable, not taxable value
 */
export function calcInvoiceTotals(lines, adjustments = {}) {
  const taxableValue = round2(sum(lines.map((l) => l.taxableValue)));
  const cgstAmount = round2(sum(lines.map((l) => l.cgstAmount)));
  const sgstAmount = round2(sum(lines.map((l) => l.sgstAmount)));
  const igstAmount = round2(sum(lines.map((l) => l.igstAmount)));

  // An invoice-level discount is applied to the already-taxed subtotal, not
  // re-run through the tax calculation — the tax was correctly assessed on
  // the goods' value at the point of sale; a discount off the final bill is a
  // reduction in what's collected, not a retroactive change to that value.
  const discountAmount = round2(adjustments.discountAmount ?? 0);
  const subtotal = taxableValue.plus(cgstAmount).plus(sgstAmount).plus(igstAmount);
  const afterDiscount = subtotal.minus(discountAmount);

  const rounded = afterDiscount.toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  const roundOff = round2(rounded.minus(afterDiscount));
  const grandTotal = round2(rounded);

  const oldGoldValue = round2(adjustments.oldGoldValue ?? 0);
  const chitAdjustValue = round2(adjustments.chitAdjustValue ?? 0);
  const amountPayable = round2(grandTotal.minus(oldGoldValue).minus(chitAdjustValue));

  return {
    taxableValue: toAmount(taxableValue),
    cgstAmount: toAmount(cgstAmount),
    sgstAmount: toAmount(sgstAmount),
    igstAmount: toAmount(igstAmount),
    discountAmount: toAmount(discountAmount),
    roundOff: toAmount(roundOff),
    grandTotal: toAmount(grandTotal),
    oldGoldValue: toAmount(oldGoldValue),
    chitAdjustValue: toAmount(chitAdjustValue),
    amountPayable: toAmount(amountPayable),
  };
}

// ---------------------------------------------------------------------------
// GSTIN validation — 15 characters: 2-digit state code, 10-character PAN,
// 1-digit entity code, 'Z' (fixed), 1 checksum character (mod-36).
// ---------------------------------------------------------------------------

const GSTIN_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const GSTIN_STRUCTURE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

/** The mod-36 checksum digit for the first 14 characters of a GSTIN. */
function gstinChecksum(gstin14) {
  let factor = 2;
  let sum14 = 0;
  const mod = GSTIN_CHARS.length;

  for (let i = gstin14.length - 1; i >= 0; i -= 1) {
    const codePoint = GSTIN_CHARS.indexOf(gstin14[i]);
    let digit = factor * codePoint;
    digit = Math.floor(digit / mod) + (digit % mod);
    sum14 += digit;
    factor = factor === 2 ? 1 : 2;
  }

  const checksumIndex = (mod - (sum14 % mod)) % mod;
  return GSTIN_CHARS[checksumIndex];
}

/**
 * True structural + checksum validation, not just a format regex. Catches a
 * mistyped digit at entry rather than at return-filing time.
 */
export function isValidGstin(gstin) {
  if (typeof gstin !== 'string') return false;
  const value = gstin.trim().toUpperCase();
  if (!GSTIN_STRUCTURE.test(value)) return false;
  return gstinChecksum(value.slice(0, 14)) === value[14];
}

export function gstinStateCode(gstin) {
  return typeof gstin === 'string' ? gstin.slice(0, 2) : null;
}

/**
 * A Zod schema for an optional GSTIN field, checksum and all.
 *
 * Every place a GSTIN is entered (customer master, shop settings, an
 * invoice's customer snapshot) must use this rather than hand-rolling the
 * structural regex again — a regex alone accepts a mistyped digit as long as
 * it's still a digit in the right position; the checksum is what actually
 * catches the typo, and it's easy to forget to wire in separately.
 *
 * Optional and blank-friendly because a walk-in (B2C) customer legitimately
 * has no GSTIN at all.
 */
export const gstinFieldSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => v === '' || isValidGstin(v), {
    message: "That GSTIN doesn't look right — check the digits and letters",
  })
  .optional()
  .or(z.literal(''));

// ---------------------------------------------------------------------------
// Amount in words — Indian numbering (lakh / crore), as required on a GST
// invoice: "Rupees Three Lakh Forty-Two Thousand One Hundred Fifty and Fifty
// Paise Only".
// ---------------------------------------------------------------------------

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function twoDigitsInWords(n) {
  if (n < 20) return ONES[n];
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return ones ? `${TENS[tens]}-${ONES[ones]}` : TENS[tens];
}

function threeDigitsInWords(n) {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  const parts = [];
  if (hundreds) parts.push(`${ONES[hundreds]} Hundred`);
  if (rest) parts.push(twoDigitsInWords(rest));
  return parts.join(' ');
}

/** Whole-number rupees, Indian grouping: crore (1e7) / lakh (1e5) / thousand / hundred. */
function integerInWords(n) {
  if (n === 0) return 'Zero';

  const crore = Math.floor(n / 1_00_00_000);
  const lakh = Math.floor((n % 1_00_00_000) / 1_00_000);
  const thousand = Math.floor((n % 1_00_000) / 1_000);
  const hundred = n % 1_000;

  const parts = [];
  if (crore) parts.push(`${threeDigitsInWords(crore)} Crore`);
  if (lakh) parts.push(`${threeDigitsInWords(lakh)} Lakh`);
  if (thousand) parts.push(`${threeDigitsInWords(thousand)} Thousand`);
  if (hundred) parts.push(threeDigitsInWords(hundred));

  return parts.join(' ');
}

/** e.g. amountInWords('342150.50') -> "Rupees Three Lakh Forty-Two Thousand One Hundred Fifty and Fifty Paise Only" */
export function amountInWords(amount) {
  const value = round2(amount);
  const rupees = value.toDecimalPlaces(0, Decimal.ROUND_DOWN).toNumber();
  const paise = value.minus(rupees).times(100).round().toNumber();

  const rupeeWords = integerInWords(rupees);
  const paiseWords = paise ? ` and ${twoDigitsInWords(paise)} Paise` : '';

  return `Rupees ${rupeeWords}${paiseWords} Only`;
}

// ---------------------------------------------------------------------------
// Section 269ST — a person may not receive ₹2,00,000 or more in cash from one
// person, whether in a single transaction or in aggregate across a day. The
// penalty is equal to the amount received, and there is no PAN or paperwork
// that cures it — the only fix is a non-cash payment mode. This is therefore
// enforced as a hard block at the amount that would breach it (₹2,00,000),
// not a warning after the fact — see services/compliance/cashCompliance.js
// for where every cash-accepting module applies it.
// ---------------------------------------------------------------------------

export const CASH_LIMIT_269ST = '200000';

/** True once the amount reaches the ₹2,00,000 line — that amount itself is already over the limit. */
export function exceeds269ST(cashReceivedToday) {
  return money(cashReceivedToday).greaterThanOrEqualTo(money(CASH_LIMIT_269ST));
}

/** A single cash payment, on its own, at or past the ₹2,00,000 line. */
export function singleCashPaymentExceeds269ST(amount) {
  return exceeds269ST(amount);
}

// ---------------------------------------------------------------------------
// Income Tax Rule 114B — PAN must be quoted for a sale or purchase of goods
// exceeding ₹2,00,000 per transaction, in any payment mode (not just cash —
// unlike §269ST above, a bank transfer or cheque for this amount is legal,
// but still requires PAN on record). Same threshold, different requirement.
// ---------------------------------------------------------------------------

export const PAN_REQUIRED_THRESHOLD = '200000';

export function requiresPan(transactionValue) {
  return money(transactionValue).greaterThanOrEqualTo(money(PAN_REQUIRED_THRESHOLD));
}

const PAN_STRUCTURE = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

export function isValidPan(pan) {
  return typeof pan === 'string' && PAN_STRUCTURE.test(pan.trim().toUpperCase());
}

/**
 * A GSTIN's first 10 characters (after the 2-digit state code) are the
 * holder's PAN — so a registered dealer's GSTIN already satisfies Rule 114B
 * without asking for PAN a second time.
 */
export function panFromGstin(gstin) {
  if (!isValidGstin(gstin)) return null;
  return gstin.trim().toUpperCase().slice(2, 12);
}

/** A Zod schema for an optional PAN field — mirrors gstinFieldSchema above. */
export const panFieldSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => v === '' || isValidPan(v), {
    message: "That PAN doesn't look right — it should read like ABCDE1234F",
  })
  .optional()
  .or(z.literal(''));
